import { useMemo } from "react";
import z from "zod";

import {
	MAX_NON_REALIZATOR_PARTICIPANTS,
	ParticipantType,
} from "CommissionDistribution/Models/types";
import {
	CommissionDistributionResource,
	useCommissionDistributionResource,
} from "CommissionDistribution/Translations/CommissionDistributionResource";

const typingSchema = z.object({
	isConfirmed: z.boolean(),
	participants: z.array(
		z.object({
			commissionDistributionParticipantId: z.string().optional(),
			participantType: z.enum([
				ParticipantType.ContractTipster,
				ParticipantType.ClientTipster,
				ParticipantType.InvitedSpecialist,
			]),
			consultantId: z.string(),
			amount: z.coerce.number(),
		}),
	),
});

export type CommissionDistributionFormValues = z.input<typeof typingSchema>;

export const useCommissionDistributionSchema = () => {
	const { t } = useCommissionDistributionResource();
	const R = CommissionDistributionResource;

	return useMemo(
		() =>
			z.object({
				isConfirmed: z.boolean(),
				participants: z
					.array(
						z.object({
							commissionDistributionParticipantId: z.string().optional(),
							participantType: z.enum([
								ParticipantType.ContractTipster,
								ParticipantType.ClientTipster,
								ParticipantType.InvitedSpecialist,
							]),
							consultantId: z
								.string()
								.min(1, t(R.Validation.ConsultantRequired)),
							amount: z.coerce
								.number({ error: t(R.Validation.AmountRequired) })
								.min(0, t(R.Validation.AmountOutOfRange))
								.max(100, t(R.Validation.AmountOutOfRange)),
						}),
					)
					.max(MAX_NON_REALIZATOR_PARTICIPANTS)
					.superRefine((participants, ctx) => {
						const sum = participants.reduce(
							(acc, p) => acc + (p.amount || 0),
							0,
						);
						if (sum > 100) {
							ctx.addIssue({
								code: "custom",
								message: t(R.Validation.SumExceedsHundred),
							});
						}

						const seen = new Set<string>();
						participants.forEach((p, idx) => {
							if (!p.consultantId) return;
							if (seen.has(p.consultantId)) {
								ctx.addIssue({
									code: "custom",
									path: [idx, "consultantId"],
									message: t(R.Validation.DuplicateConsultant),
								});
							}
							seen.add(p.consultantId);
						});
					}),
			}),
		[t],
	);
};
